import { ReactNode, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { updateUserOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import {
  GettingStartedOnboardingStepId,
  GettingStartedOnboardingStep,
  GettingStartedUserOnboardingStepExpanded,
} from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { selectGettingStartedUserOnboardingSteps } from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { ViewMissingStatementsAccordionForCatchupBookkeeping } from './OnboardingItemCTAContent'
import { useHeardPlaid } from '@/components/plaid/helpers'
import { Badge, TaskItem, TaskItemProps } from '@/ui'
import { getUserNeedsClarificationTransactionsSelector } from '@/features/transactions/transactions.selectors'
import {
  USER_TRANSACTION_KEY,
  fetchUserTransactions,
} from '@/features/transactions/transactions.slice'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectShouldShowCubkDeadlineBanner,
  EXPEDITE_CARD_IDS,
} from '../cubk-deadline'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { selectCatchUpBookkeepingStatusForCurrentUser } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { useConnectHeardBankItemConfig } from './useConnectHeardBankItemConfig'
import {
  OFFER_SOURCES,
  useBankingAnalytics,
} from '@/features/banking/analytics'
import { get1099NecThresholdDisplay } from '@/features/taxes/annual-taxes/constants'

type OnboardingItemContent = Omit<
  TaskItemProps,
  'started' | 'completed' | 'skipped'
>

type StepWithContentId = Exclude<
  GettingStartedOnboardingStepId,
  typeof GettingStartedOnboardingStep.joinGroupOnboardingSession
>

const hasContent = (
  stepId: GettingStartedOnboardingStepId
): stepId is StepWithContentId =>
  stepId !== GettingStartedOnboardingStep.joinGroupOnboardingSession

export const useGettingStartedOnboardingSteps = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const gettingStartedSteps = useReselector(
    selectGettingStartedUserOnboardingSteps
  )
  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const needsClarificationTransactions = useReselector(
    getUserNeedsClarificationTransactionsSelector
  )
  const isLoadingTransactions = useReselector(
    getIsFetchingOrNotStarted,
    USER_TRANSACTION_KEY
  )
  const shouldShowUrgentBadge = useReselector(
    selectShouldShowCubkDeadlineBanner
  )

  // Selectors for reskinned onboarding (ivl-dashboard-m4)
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const cubkStatus = useReselector(selectCatchUpBookkeepingStatusForCurrentUser)
  const connectHeardBankConfig = useConnectHeardBankItemConfig()

  const { trackOfferViewed } = useBankingAnalytics()

  // Fire Banking Offer Viewed once when the connectHeardBank step is in the
  // user's getting-started list (it only appears post-approval, so this is
  // effectively "user has seen the connect-bank nudge"). useRef sentinel
  // prevents re-fires on step refetches that briefly flip the boolean off
  // and back on.
  const heardBankStepVisible = gettingStartedSteps.some(
    (step) =>
      step.onboardingStep.identifier ===
      GettingStartedOnboardingStep.connectHeardBank
  )
  const heardBankOfferTrackedRef = useRef(false)
  useEffect(() => {
    if (heardBankStepVisible && !heardBankOfferTrackedRef.current) {
      heardBankOfferTrackedRef.current = true
      trackOfferViewed(OFFER_SOURCES.GettingStartedChecklist)
    }
  }, [heardBankStepVisible, trackOfferViewed])

  // Needed for CLARIFY step visibility
  useEffect(() => {
    dispatch(fetchUserTransactions())
  }, [dispatch])

  const missingStatementLink = '/practice/statements'
  const ten99DueAt = annualTaxDetails?.form_1099_nec_due_date

  const { open: openPlaidModal } = useHeardPlaid({
    redirectLink: '',
  })

  const clarifyDescription = isLoadingTransactions
    ? 'We need your input on some transactions to ensure accuracy of your books.'
    : 'Your bookkeeper reviewed your transactions but needs your input on a few before they can finalize your books.'

  const gettingStartedContentMapping: Record<
    StepWithContentId,
    OnboardingItemContent
  > = {
    [GettingStartedOnboardingStep.uploadBankStatements]: {
      header: 'Fill in transaction gaps',
      description:
        "Upload bank statements for any months we're missing so your books reflect the full year.",
      additionalDetails: (
        <ViewMissingStatementsAccordionForCatchupBookkeeping />
      ),
      onActionClick: () => {
        navigate(missingStatementLink, {
          state: {
            initiatedBy: GettingStartedOnboardingStep.uploadBankStatements,
            fromGettingStarted: true,
          },
        })
      },
    },
    [GettingStartedOnboardingStep.clarifyUncategorizedTransactions]: {
      header: 'Review unclear transactions',
      description: clarifyDescription,
      onActionClick: () => {
        navigate('/transactions/review')
      },
    },
    [GettingStartedOnboardingStep.connectBusinessBankAccount]: {
      header: 'Connect bank accounts',
      description:
        'Connect your bank so Heard can automatically sync your transactions and start your bookkeeping.',
      onActionClick: () => {
        openPlaidModal()
      },
    },
    [GettingStartedOnboardingStep.connectHeardBank]: {
      ...connectHeardBankConfig,
    },
    [GettingStartedOnboardingStep.yearEndWrapUpSurvey]: {
      header: 'Tell us your tax needs',
      description:
        'Personalize your tax season, and we’ll guide you through every step and deadline to keep you on track. We recommend completing this ASAP. It only takes 5 minutes!',
      onActionClick: () => navigate('/taxes/annual'),
      headerExtra: (
        <Badge color="orange" variant="secondary">
          ASAP
        </Badge>
      ),
    },
    [GettingStartedOnboardingStep.complete_1099_survey]: {
      header: 'Stay compliant with contractors',
      description: (
        <>
          Let us know if you paid anyone{' '}
          {get1099NecThresholdDisplay(currentTaxYear)}+ in {currentTaxYear}.
          We&apos;ll handle the 1099s from there.
        </>
      ),
      onActionClick: () => navigate('/taxes/annual'),
      headerExtra: (
        <Badge color="orange" variant="secondary">
          Due{' '}
          {ten99DueAt &&
            convertUtcToLocalDate(ten99DueAt)?.toFormat(
              DATE_FORMATS_LUXON.MONTH_DAY
            )}
        </Badge>
      ),
    },
    [GettingStartedOnboardingStep.payrollInterest]: {
      header: 'Tell us your payroll needs',
      description:
        'Running an S-corp means paying yourself a reasonable salary via payroll. Share your current status with us, and learn about payroll with Heard.',
      onActionClick: () => navigate('/payroll/interest?screen=intro'),
    },
    [GettingStartedOnboardingStep.markPersonalTransactions]: {
      header: 'Clean books of personal charges',
      description:
        "Flag personal charges so they stay out of your books and don't inflate your tax bill.",
      onActionClick: () => navigate('/transactions'),
    },
  }

  const getTaskItemProps = (
    step: GettingStartedUserOnboardingStepExpanded,
    content: OnboardingItemContent
  ): TaskItemProps => {
    const onActionClick = () => {
      if (!step.startedAt) {
        dispatch(
          updateUserOnboardingStep({
            id: step.id,
            updatableFields: { startedAt: DateTime.now().toISO() },
          })
        )
      }
      content.onActionClick()
    }

    // Add Urgent badge for EXPEDITE cards when deadline banner is shown
    const isExpediteCard = EXPEDITE_CARD_IDS.includes(
      step.onboardingStep.identifier
    )
    const urgentBadge =
      shouldShowUrgentBadge && isExpediteCard ? (
        <Badge color="orange">Urgent</Badge>
      ) : undefined

    // Combine urgent badge with existing headerExtra if both exist
    const headerExtra =
      urgentBadge && content.headerExtra ? (
        <>
          {urgentBadge}
          {content.headerExtra}
        </>
      ) : (
        urgentBadge || content.headerExtra
      )

    return {
      ...content,
      started: Boolean(step.startedAt),
      completed: Boolean(step.completedAt),
      skipped: Boolean(step.skippedAt),
      headerExtra,
      onActionClick,
    }
  }

  const followupGatedSteps = gettingStartedSteps.filter((step) => {
    if (
      step.onboardingStep.identifier ===
      GettingStartedOnboardingStep.clarifyUncategorizedTransactions
    ) {
      return (
        needsClarificationTransactions.length > 0 &&
        Boolean(cubkStatus?.followupRequestedAt)
      )
    }
    return true
  })

  // Hide SET_UP_PAYROLL for non-S-Corp users
  const visibleSteps = followupGatedSteps.filter(
    (step) =>
      step.onboardingStep.identifier !==
        GettingStartedOnboardingStep.payrollInterest || isScorp
  )

  // Steps without content (JOIN_GROUP_ONBOARDING_SESSION) have no card
  return visibleSteps.reduce(
    (acc, step) => {
      const stepId = step.onboardingStep.identifier
      if (!hasContent(stepId)) {
        return acc
      }

      return {
        ...acc,
        [stepId]: (
          <TaskItem
            {...getTaskItemProps(step, gettingStartedContentMapping[stepId])}
          />
        ),
      }
    },
    {} as Record<GettingStartedOnboardingStepId, ReactNode>
  )
}
